import StateObserver, { user } from 'src/StateObserver';
import PopupCheats, { Item } from 'src/game/cheats/PopupCheats';
import { CheatData } from 'src/game/cheats/cheats.sequences';
import {
  closeAllPopups,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { cardsGetOwnedCards } from 'src/replicant/cards/cards.getters';
import { CardId, cardsCatalog } from 'src/replicant/cards/cards.ruleset';
import type { PopupCardPickerOpenArgs } from './popups/PopupCardPicker';

const MATCH2_CARDS = 'match2 Cards';

/** Keep the card picker above the cheat popup and cheat navigation buttons. */
const CHEAT_CARD_PICKER_Z_INDEX = 20001;

/**
 * Open the native card picker above the cheat UI.
 *
 * @param opts Card picker title, button label, and selectable cards.
 * @returns The selected card ID, or undefined when cancelled.
 */
async function popupSelectCard(
  opts: Omit<PopupCardPickerOpenArgs, 'zIndex'>,
): Promise<CardId | undefined> {
  // Keep the cheats popup open underneath the card picker for repeated testing.
  const selectedCard = await openPopupPromise('popupCardPicker', {
    ...opts,
    zIndex: CHEAT_CARD_PICKER_Z_INDEX,
  });

  // Card cheats only need the selected card identifier.
  return selectedCard?.id;
}

const list = () => {
  const cheats: Record<string, CheatData[]> = {};

  cheats[MATCH2_CARDS] = [
    {
      text: 'Reset Cards',
      color: 'cancel',
      cheat: async () => {
        closeAllPopups();
        await StateObserver.invoke.cheat_cardsReset();
      },
    },
    {
      text: 'Add Card',
      color: 'secondary',
      cheat: async () => {
        // Show every catalog card with artwork while retaining useful owned counts.
        const allCards = Object.keys(cardsCatalog) as CardId[];
        const ownedCards = cardsGetOwnedCards(user());
        const cards = allCards.map((cardId) => ({
          id: cardId,
          count: Math.max(1, ownedCards[cardId] ?? 0),
        }));

        // Let the tester choose the card to grant.
        const cardId = await popupSelectCard({
          title: 'Add Card',
          cards,
          buttonText: 'Add',
        });
        if (!cardId) return;

        // Grant the selected card.
        await StateObserver.invoke.cheat_cardsAddCard({
          cardId,
        });
      },
    },
    {
      text: 'Remove Card',
      color: 'secondary',
      cheat: async () => {
        const ownedCards = cardsGetOwnedCards(user());
        const ownedCardsIds = Object.keys(ownedCards) as CardId[];

        if (ownedCardsIds.length === 0) {
          window.alert('No cards to remove');
          return;
        }

        const cards = ownedCardsIds.map((cardId) => ({
          id: cardId,
          count: ownedCards[cardId] ?? 0,
        }));

        const cardId = await popupSelectCard({
          title: 'Remove Card',
          cards,
          buttonText: 'Remove',
        });
        if (!cardId) return;

        await StateObserver.invoke.cheat_cardsRemoveCard({
          cardId,
        });
      },
    },
    {
      text: 'Receive 5 Random Cards',
      color: 'secondary',
      cheat: async () => {
        const allCards = Object.keys(cardsCatalog) as CardId[];
        const shuffledCards = allCards.sort(() => Math.random() - 0.5);
        const cardIds = shuffledCards.slice(0, 5);
        await StateObserver.invoke.cheat_cardsAddCardsReceived({
          cardIds,
        });
        closeAllPopups();
      },
    },
    {
      text: 'Reset Limits',
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_cardsResetTrades();
        closeAllPopups();
      },
    },
    {
      text: 'Max Limits',
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_cardsMaxTrades();
        closeAllPopups();
      },
    },
    {
      text: 'Complete album',
      color: 'secondary',
      cheat: async () => {
        const allCards = Object.keys(cardsCatalog) as CardId[];
        for (const cardId of allCards) {
          const owned = cardsGetOwnedCards(user());
          if (owned[cardId]) continue;
          await StateObserver.invoke.cheat_cardsAddCard({
            cardId,
          });
        }
        closeAllPopups();
      },
    },
    {
      text: 'Get Pack Tier 5',
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_cardsGrantPack({ tier: 5 });
      },
    },
  ];

  return cheats;
};

const ui = (args: {
  key: string;
  popup: PopupCheats;
  item: Item;
  title: LangBitmapFontTextView;
  setting: CheatData[];
  parent: Item | null;
}) => {
  if (args.key !== MATCH2_CARDS) return;
  args.title.updateOpts({
    width: args.item.style.width - 40,
    height: 20,
    y: 8,
    align: 'center',
  });
  args.popup.createVerticalButtons(args.item, args.setting);
};

export const match2CardsCheats = {
  list,
  ui,
};
